/**
 * App 根元件：套上 Ionic 外殼、擋登入狀態、掛上路由。
 *
 * **登入畫面直接用 `@shared/auth-ui`**（framework:agnostic）。Ionic 負責的是原生
 * 的操作手感（轉場、手勢、safe-area），不需要把每個按鈕都換成 Ionic 元件。
 */

import { IonApp, IonRouterOutlet } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Redirect, Route } from 'react-router-dom';
import { AuthGate } from '@shared/auth-ui';
import { QueryProvider } from '@shared/query';
import { AmbientBackground, InstallPrompt, OfflineNotice } from '@shared/ui';
import { DietApp } from '@diet/components/diet-app';
import { UtensilsIcon } from '@diet/components/icons';
import { AppLoadingScreen } from '@diet/components/app-loading-screen';

export function App() {
  return (
    <IonApp>
      <AmbientBackground />
      <AuthGate
        appName="飲食記錄"
        description="記錄每日飲食，追蹤熱量與營養攝取。"
        /* 確認登入狀態與登入後抓紀錄要長得一樣，否則會閃兩種載入畫面 */
        fallback={<AppLoadingScreen />}
        icon={<UtensilsIcon size={28} />}
        /*
          登入頁也要提示安裝：第一次來的人看到的就是這一頁，而那正是他在決定
          「要不要把這個東西留在手機上」的時候。這一頁沒有 FAB，卡片貼回底部即可。
        */
        overlay={
          <InstallPrompt appName="飲食記錄" icon={<UtensilsIcon size={22} />} />
        }
      >
        {/*
          登入後的版本要抬到記錄鍵上方（FAB 在 bottom 1.5rem、高 3.25rem），
          否則卡片會蓋住這個 app 唯一的主要動作。
        */}
        <InstallPrompt
          appName="飲食記錄"
          icon={<UtensilsIcon size={22} />}
          className="bottom-[calc(5.5rem+env(safe-area-inset-bottom))]"
        />

        {/*
          離線提示。紀錄不快取（見 sw.ts），離線時什麼都存不進去也讀不回來，
          故這裡把話講明，而不是讓使用者從一連串失敗自己推論。

          位置要同時讓開兩個東西：安裝卡片（讀它發佈的 --install-prompt-inset，
          疊在它上面 0.75rem）與記錄鍵（安裝卡片不在時的 fallback）。
        */}
        <OfflineNotice
          description="新增與修改需要連線才能儲存，恢復後畫面會自動更新。"
          className="bottom-[calc(var(--install-prompt-inset,calc(5.5rem+env(safe-area-inset-bottom)))+0.75rem)]"
        />

        {/*
          路由整組放在 AuthGate 之內：未登入時沒有任何頁面該被掛載，登入頁本身也
          不是一個 route——它是閘門，不是目的地。網址在被擋下的期間不會被改寫，
          登入完成後仍會落在原本的深連結上。
        */}
        {/*
          QueryProvider 在 AuthGate 之內：未登入時沒有任何查詢該被發出，而快取的
          生命週期本來就該與「這一次登入」對齊——被 session 失效清空（見該元件）、
          隨登入頁換上而重建。
        */}
        <QueryProvider devtools={import.meta.env.DEV}>
          <IonReactRouter>
            {/*
            **頁面轉場、返回手勢與 page stack 全部由 IonRouterOutlet 提供**，
            這也是選 `@ionic/react-router`（綁 react-router v5）而非其他 router 的
            唯一理由——它是接得上 Ionic 轉場的那一個。

            IonRouterOutlet 會自行檢視直屬子節點的 `path` 來決定推入／彈出哪個頁面，
            故子節點只能是 Route／Redirect，中間不可再包一層元件。
            每個 route 的元件本身必須是 IonPage，否則轉場沒有可動的容器。
          */}
            <IonRouterOutlet>
              <Route path="/" exact component={DietApp} />

              {/*
                未知路徑一律回首頁。包在 Route 裡而非裸寫 Redirect：裸的 Redirect
                沒有 path，IonRouterOutlet 比對不到，會在每次轉場時把它當成候選頁。
              */}
              <Route render={() => <Redirect to="/" />} />
            </IonRouterOutlet>
          </IonReactRouter>
        </QueryProvider>
      </AuthGate>
    </IonApp>
  );
}
